import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useBranding } from '../../hooks/useBranding';
import CategoryManager from './CategoryManager';
import { Plus, Link as LinkIcon, Unlink } from 'lucide-react';
import ImageLightbox from '../../components/ImageLightbox';

const TIER_BREAKS_PER_M = [
  { label: '20M',    from: 20000,   to: 49999 },
  { label: '50M',    from: 50000,   to: 99999 },
  { label: '100M',   from: 100000,  to: 249999 },
  { label: '250M',   from: 250000,  to: 499999 },
  { label: '500M',   from: 500000,  to: 999999 },
  { label: '1,000M', from: 1000000, to: 2499999 },
  { label: '2,500M', from: 2500000, to: 4999999 },
  { label: '5,000M+',from: 5000000, to: null },
];

// For per_case products, tiers are simply case quantity breaks — TTC enters them freely
function TierEditor({ product, onClose }) {
  const isPerCase = product.price_unit === 'per_case';
  const defaultBreaks = isPerCase
    ? [{ from: 1, to: null, price: '' }]
    : TIER_BREAKS_PER_M.map(b => ({ ...b, price: '' }));

  const [tiers, setTiers] = useState(defaultBreaks);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/api/products/${product.id}/tiers`),
      api.get('/api/customers'),
    ]).then(([t, c]) => {
      setCustomers(c.data.customers);
      const existing = t.data.tiers.filter(x => !x.customer_id);
      if (existing.length > 0) {
        if (isPerCase) {
          setTiers(existing.map(x => ({ from: x.qty_from, to: x.qty_to, price: x.price?.toFixed(2) || '' })));
        } else {
          setTiers(TIER_BREAKS_PER_M.map(b => ({
            ...b,
            price: existing.find(x => x.qty_from === b.from)?.price?.toFixed(2) || '',
          })));
        }
      }
      setLoading(false);
    });
  }, [product.id]);

  const loadForCustomer = (cid) => {
    setCustomerId(cid);
    api.get(`/api/products/${product.id}/tiers`).then(r => {
      const relevant = r.data.tiers.filter(x => cid ? x.customer_id === parseInt(cid) : !x.customer_id);
      if (relevant.length > 0) {
        if (isPerCase) {
          setTiers(relevant.map(x => ({ from: x.qty_from, to: x.qty_to, price: x.price?.toFixed(2) || '' })));
        } else {
          setTiers(TIER_BREAKS_PER_M.map(b => ({
            ...b,
            price: relevant.find(x => x.qty_from === b.from)?.price?.toFixed(2) || '',
          })));
        }
      } else {
        setTiers(isPerCase ? [{ from: 1, to: null, price: '' }] : TIER_BREAKS_PER_M.map(b => ({ ...b, price: '' })));
      }
    });
  };

  const addCaseTier = () => {
    setTiers(prev => [...prev, { from: '', to: null, price: '' }]);
  };

  const removeCaseTier = (i) => {
    setTiers(prev => prev.filter((_, j) => j !== i));
  };

  const updateTier = (i, field, val) => {
    setTiers(prev => prev.map((t, j) => j === i ? { ...t, [field]: val } : t));
  };

  const save = async () => {
    const validTiers = tiers.filter(t => t.price !== '' && !isNaN(t.price) && t.from !== '');
    if (!validTiers.length) return toast.error('Enter at least one price');
    await api.put(`/api/products/${product.id}/tiers`, {
      customer_id: customerId || null,
      tiers: validTiers.map(t => ({
        from: parseInt(t.from), to: t.to ? parseInt(t.to) : null, price: parseFloat(t.price),
      })),
    });
    toast.success('Pricing saved');
    onClose();
  };

  const priceLabel = isPerCase ? 'Price per Case ($)' : 'Price per M ($)';
  const qtyLabel = isPerCase ? 'Cases From' : 'Qty Break';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Edit Price Tiers — {product.name}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 6, padding: '8px 12px', marginBottom: 14, fontSize: 12, color: 'var(--gray-600)' }}>
          Unit: <strong>{isPerCase ? 'Per Case' : 'Per M (per 1,000 units)'}</strong>
          {product.case_qty_label && <span> · Case contains: {product.case_qty_label}</span>}
        </div>
        {loading ? <div className="loading">Loading…</div> : (
          <>
            <div className="form-group">
              <label className="form-label">Customer (blank = default pricing for all)</label>
              <select className="form-select" value={customerId} onChange={e => loadForCustomer(e.target.value)}>
                <option value="">Default / All Customers</option>
                {customers.filter(c => c.active).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <table style={{ width: '100%', marginBottom: 8 }}>
              <thead>
                <tr>
                  <th style={{ width: isPerCase ? '35%' : '45%' }}>{qtyLabel}</th>
                  {isPerCase && <th style={{ width: '25%' }}>Cases To</th>}
                  <th>{priceLabel}</th>
                  {isPerCase && <th style={{ width: 40 }}></th>}
                </tr>
              </thead>
              <tbody>
                {tiers.map((t, i) => (
                  <tr key={i}>
                    <td style={{ padding: '4px 8px' }}>
                      {isPerCase
                        ? <input type="number" className="form-input" min="1" value={t.from}
                            onChange={e => updateTier(i, 'from', e.target.value)} />
                        : <span style={{ fontSize: 13, fontWeight: 500 }}>{t.label}</span>
                      }
                    </td>
                    {isPerCase && (
                      <td style={{ padding: '4px 8px' }}>
                        <input type="number" className="form-input" min="1" placeholder="∞"
                          value={t.to || ''} onChange={e => updateTier(i, 'to', e.target.value || null)} />
                      </td>
                    )}
                    <td style={{ padding: '4px 8px' }}>
                      <input type="number" className="form-input" step="0.01" min="0"
                        value={t.price} onChange={e => updateTier(i, 'price', e.target.value)} />
                    </td>
                    {isPerCase && (
                      <td style={{ padding: '4px 8px' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => removeCaseTier(i)}>✕</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {isPerCase && (
              <button className="btn btn-secondary btn-sm" onClick={addCaseTier}>
                <Plus size={13} /> Add Price Break
              </button>
            )}
          </>
        )}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save}>Save Pricing</button>
        </div>
      </div>
    </div>
  );
}

function AssignmentEditor({ product, onClose }) {
  const [allCustomers, setAllCustomers] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => Promise.all([
    api.get('/api/customers'),
    api.get(`/api/products/${product.id}/assignments`),
  ]).then(([c, a]) => {
    setAllCustomers(c.data.customers.filter(x => x.active));
    setAssigned(a.data.assignments.map(x => x.id));
    setLoading(false);
  });

  useEffect(() => { load(); }, [product.id]);

  const toggle = async (customerId, isAssigned) => {
    if (isAssigned) {
      await api.delete(`/api/products/${product.id}/assignments/${customerId}`);
      toast.success('Assignment removed');
    } else {
      await api.post(`/api/products/${product.id}/assignments`, { customer_id: customerId });
      toast.success('Customer assigned');
    }
    load();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Customer Assignments — {product.name}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <p className="text-sm text-muted" style={{ marginBottom: 14 }}>
          Assign this product to specific customers. If the product is marked Generic, all customers with the generic flag enabled can also see it.
        </p>
        {loading ? <div className="loading">Loading…</div> : (
          <table>
            <thead><tr><th>Customer</th><th>Assigned</th><th></th></tr></thead>
            <tbody>
              {allCustomers.map(c => {
                const isAssigned = assigned.includes(c.id);
                return (
                  <tr key={c.id}>
                    <td><strong>{c.name}</strong></td>
                    <td>{isAssigned ? <span className="badge badge-active">Assigned</span> : <span className="text-muted text-sm">—</span>}</td>
                    <td>
                      <button className={`btn btn-sm ${isAssigned ? 'btn-danger' : 'btn-secondary'}`}
                        onClick={() => toggle(c.id, isAssigned)}>
                        {isAssigned ? <><Unlink size={12} /> Remove</> : <><LinkIcon size={12} /> Assign</>}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

function ProductEditor({ product, categories, onClose }) {
  const isNew = !product;
  const [f, setF] = useState({
    sku: product?.sku || '', name: product?.name || '', description: product?.description || '',
    category: product?.category || '', price_unit: product?.price_unit || 'per_m',
    case_qty: product?.case_qty || '', case_qty_label: product?.case_qty_label || '',
    is_generic: product?.is_generic ?? 1, active: product?.active ?? 1,
    attribute_set_id: product?.attribute_set_id || '',
    qty_min: product?.qty_min || '', qty_max: product?.qty_max || '', qty_step: product?.qty_step || '',
    quote_required: product?.quote_required ?? 0,
  });
  // Per-item size list override, edited as one size per line (stored as JSON).
  const [sizeText, setSizeText] = useState(() => {
    try { return (JSON.parse(product?.size_override || '[]') || []).join('\n'); } catch (_) { return ''; }
  });
  const [sets, setSets] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(product?.image_url || null);
  const [removeImg, setRemoveImg] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));

  useEffect(() => { api.get('/api/products/attribute-sets/all').then(r => setSets(r.data.sets || [])).catch(() => {}); }, []);

  const onPick = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) return toast.error('Use a JPG, PNG, or WebP image');
    if (file.size > 5 * 1024 * 1024) return toast.error('Image must be under 5MB');
    setImageFile(file); setRemoveImg(false); setPreview(URL.createObjectURL(file));
  };
  const clearImage = () => { setImageFile(null); setPreview(null); setRemoveImg(true); };

  const save = async () => {
    if (!f.sku || !f.name) return toast.error('SKU and name are required');
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v === null ? '' : v));
      const sizes = sizeText.split('\n').map(s => s.trim()).filter(Boolean);
      fd.set('size_override', sizes.length ? JSON.stringify(sizes) : '');
      if (imageFile) fd.append('image', imageFile);
      if (removeImg && !imageFile) fd.append('remove_image', 'true');
      const cfg = { headers: { 'Content-Type': 'multipart/form-data' } };
      if (isNew) await api.post('/api/products', fd, cfg);
      else await api.patch(`/api/products/${product.id}`, fd, cfg);
      toast.success(isNew ? 'Item created' : 'Item updated');
      onClose(true);
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={() => onClose(false)}>
      <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isNew ? 'New Item' : `Edit — ${product.name}`}</h3>
          <button className="btn btn-ghost btn-sm" onClick={() => onClose(false)}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group"><label className="form-label">SKU<span className="required">*</span></label>
              <input className="form-input" value={f.sku} onChange={e => set('sku', e.target.value)} />
              {!isNew && <p className="form-hint">Changing the SKU re-keys this item for future imports.</p>}</div>
            <div className="form-group"><label className="form-label">Name<span className="required">*</span></label>
              <input className="form-input" value={f.name} onChange={e => set('name', e.target.value)} /></div>
          </div>
          <div className="form-group"><label className="form-label">Description</label>
            <textarea className="form-textarea" value={f.description} onChange={e => set('description', e.target.value)} /></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Category</label>
              <input className="form-input" list="ttc-cats" value={f.category} onChange={e => set('category', e.target.value)} />
              <datalist id="ttc-cats">{categories.map(c => <option key={c} value={c} />)}</datalist></div>
            <div className="form-group"><label className="form-label">Attribute set</label>
              <select className="form-select" value={f.attribute_set_id} onChange={e => set('attribute_set_id', e.target.value)}>
                <option value="">— none —</option>
                {sets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Pricing unit</label>
              <select className="form-select" value={f.price_unit} onChange={e => set('price_unit', e.target.value)}>
                <option value="per_m">Per M (per 1,000)</option>
                <option value="per_case">Per Case</option></select></div>
            <div className="form-group"><label className="form-label">Case qty</label>
              <input className="form-input" type="number" value={f.case_qty} onChange={e => set('case_qty', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Case label</label>
              <input className="form-input" value={f.case_qty_label} onChange={e => set('case_qty_label', e.target.value)} placeholder="e.g. 8 rolls" /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Visibility</label>
              <select className="form-select" value={f.is_generic} onChange={e => set('is_generic', parseInt(e.target.value))}>
                <option value={1}>Generic (all customers)</option>
                <option value={0}>Assigned (specific customers)</option></select></div>
            <div className="form-group"><label className="form-label">Status</label>
              <select className="form-select" value={f.active} onChange={e => set('active', parseInt(e.target.value))}>
                <option value={1}>Active</option><option value={0}>Inactive</option></select></div>
          </div>
          {/* v1.44.0 — items with no contract price. Orderable, but no number
              is shown or totalled anywhere; Toledo Ticket quotes it by hand. */}
          <div className="form-group" style={{ borderTop: '1px solid var(--gray-200)', paddingTop: 12 }}>
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <input type="checkbox" checked={!!f.quote_required} style={{ marginTop: 3 }}
                onChange={e => set('quote_required', e.target.checked ? 1 : 0)} />
              <span><strong>Toledo Ticket provides custom pricing</strong><br />
                <span className="form-hint">
                  For items with no contract price. The customer can order it, but no price is
                  shown and it is left out of the order total — the order is flagged for quoting.
                </span>
              </span>
            </label>
          </div>
          <details style={{ borderTop: '1px solid var(--gray-200)', paddingTop: 12, marginBottom: 4 }}
            open={!!(f.qty_min || f.qty_max || f.qty_step || sizeText)}>
            <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--gray-600)', marginBottom: 10 }}>
              Custom order rules (optional)
            </summary>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Minimum quantity</label>
                <input className="form-input" type="number" min="0" value={f.qty_min}
                  onChange={e => set('qty_min', e.target.value)} placeholder="e.g. 20000" /></div>
              <div className="form-group"><label className="form-label">Maximum quantity</label>
                <input className="form-input" type="number" min="0" value={f.qty_max}
                  onChange={e => set('qty_max', e.target.value)} placeholder="e.g. 500000" /></div>
              <div className="form-group"><label className="form-label">Quantity step</label>
                <input className="form-input" type="number" min="0" value={f.qty_step}
                  onChange={e => set('qty_step', e.target.value)} placeholder="e.g. 1000" /></div>
            </div>
            <div className="form-group"><label className="form-label">Size list override</label>
              <textarea className="form-textarea" rows={4} value={sizeText}
                onChange={e => setSizeText(e.target.value)} placeholder={'3x5\n4x5-1/2\n3x6-1/2'} />
              <p className="form-hint">
                One size per line. Leave blank to use the shared part-count size lists from the Question Builder.
              </p>
            </div>
          </details>
          <div className="form-group"><label className="form-label">Product image</label>
            <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
              <div style={{ width: 72, height: 72, borderRadius: 8, border: '1px solid var(--gray-200)', background: 'var(--gray-50)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {preview ? <img src={preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span className="text-muted" style={{ fontSize: 11 }}>No image</span>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onPick} />
                {preview && <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--brand-red)' }} onClick={clearImage}>Remove image</button>}
              </div>
            </div>
            <p className="form-hint">JPG, PNG, or WebP, up to 5MB. Shown to customers on the catalog and order screens.</p>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => onClose(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : (isNew ? 'Create item' : 'Save changes')}</button>
        </div>
      </div>
    </div>
  );
}

export default function ProductsAdminPage() {
  const nav = useNavigate();
  const { defaultProductImage } = useBranding();
  const imgOf = (p) => p.image_url || defaultProductImage || '/default-ticket.svg';
  const [products, setProducts] = useState([]);
  const [zoomSrc, setZoomSrc] = useState(null);
  const [editing, setEditing] = useState(null); // 'new' | product object | null
  const [showCats, setShowCats] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tierProduct, setTierProduct] = useState(null);
  const [assignProduct, setAssignProduct] = useState(null);
  const [search, setSearch] = useState('');
  const [filterUnit, setFilterUnit] = useState('');

  useEffect(() => {
    api.get('/api/products').then(r => {
      setProducts(r.data.products);
      setLoading(false);
    });
  }, []);

  const reload = () => api.get('/api/products').then(r => setProducts(r.data.products));

  const categories = [...new Set(products.map(p => p.category).filter(Boolean))].sort();

  const filtered = products.filter(p => {
    const q = search.toLowerCase();
    const matchSearch = !search || p.name.toLowerCase().includes(q) || p.sku?.includes(q);
    const matchUnit = !filterUnit || p.price_unit === filterUnit;
    return matchSearch && matchUnit;
  });

  const toggleActive = async (p) => {
    await api.patch(`/api/products/${p.id}`, { active: p.active ? 0 : 1 });
    setProducts(prev => prev.map(x => x.id === p.id ? { ...x, active: x.active ? 0 : 1 } : x));
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Permanently delete "${p.name}" (${p.sku})? This can't be undone. Items on existing orders can't be deleted — deactivate them instead.`)) return;
    try {
      await api.delete(`/api/products/${p.id}`);
      toast.success('Item deleted');
      setProducts(prev => prev.filter(x => x.id !== p.id));
    } catch (e) { toast.error(e.response?.data?.error || 'Delete failed'); }
  };

  return (
    <>
      <div className="topbar"><h2>Item Manager</h2></div>
      <div className="page">
        <div className="filter-bar">
          <input className="form-input" placeholder="Search…" value={search}
            onChange={e => setSearch(e.target.value)} style={{ width: 240 }} />
          <select className="form-select" value={filterUnit} onChange={e => setFilterUnit(e.target.value)} style={{ width: 160 }}>
            <option value="">All Units</option>
            <option value="per_m">Per M (LAZ-style)</option>
            <option value="per_case">Per Case (Colonial-style)</option>
          </select>
          <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setShowCats(true)}>
            Categories
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => nav('/admin/bulk-images')}>
            Bulk Images
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
            + New Item
          </button>
        </div>
        <div className="card" style={{ padding: 0 }}>
          {loading ? <div className="loading">Loading…</div> : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Product</th>
                    <th>SKU</th>
                    <th>Category</th>
                    <th>Unit</th>
                    <th>Case Contents</th>
                    <th>Type</th>
                    <th>Customers</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(p => (
                    <tr key={p.id}>
                      <td>
                        <div onClick={() => setZoomSrc(imgOf(p))} title="Click to enlarge"
                          style={{ width: 40, height: 40, borderRadius: 6, overflow: 'hidden', background: 'var(--gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-in' }}>
                          <img src={imgOf(p)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                      </td>
                      <td><strong>{p.name}</strong></td>
                      <td className="text-muted text-sm" style={{ fontFamily: 'monospace' }}>{p.sku}</td>
                      <td className="text-sm">{p.category}</td>
                      <td>
                        <span className={`badge ${p.price_unit === 'per_case' ? 'b-pro' : 'b-sub'}`}
                          style={{ fontSize: 11 }}>
                          {p.price_unit === 'per_case' ? 'Per Case' : 'Per M'}
                        </span>
                      </td>
                      <td className="text-sm text-muted">{p.case_qty_label || '—'}</td>
                      <td>
                        <span className={`badge ${p.is_generic ? 'b-act' : 'b-sub'}`} style={{ fontSize: 11 }}>
                          {p.is_generic ? 'Generic' : 'Assigned'}
                        </span>
                      </td>
                      <td className="text-sm text-muted">
                        {p.is_generic ? 'All' :
                          p.assigned_customers?.length
                            ? p.assigned_customers.map(c => c.name).join(', ')
                            : '—'}
                      </td>
                      <td>
                        <span className={`badge badge-${p.active ? 'active' : 'inactive'}`}>
                          {p.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="actions-cell">
                          <button className="btn btn-secondary btn-sm" onClick={() => setEditing(p)}>Edit</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => setTierProduct(p)}>
                            Pricing
                          </button>
                          {!p.is_generic && (
                            <button className="btn btn-secondary btn-sm" onClick={() => setAssignProduct(p)}>
                              <LinkIcon size={12} /> Assign
                            </button>
                          )}
                          <button className="btn btn-ghost btn-sm" onClick={() => toggleActive(p)}>
                            {p.active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--brand-red)' }} onClick={() => deleteProduct(p)}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {tierProduct && <TierEditor product={tierProduct} onClose={() => { setTierProduct(null); reload(); }} />}
        {assignProduct && <AssignmentEditor product={assignProduct} onClose={() => { setAssignProduct(null); reload(); }} />}
        {editing && <ProductEditor product={editing === 'new' ? null : editing} categories={categories} onClose={(changed) => { setEditing(null); if (changed) reload(); }} />}
        {showCats && <CategoryManager onClose={() => { setShowCats(false); reload(); }} />}
        {zoomSrc && <ImageLightbox src={zoomSrc} onClose={() => setZoomSrc(null)} />}
      </div>
    </>
  );
}
